import React from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useWizard } from 'react-use-wizard';

import classNames from 'classnames';
import { AMPLITUDE_EVENTS } from '../../../../constants/analytics';
import { MEMBERSHIP } from '../../../../constants/membership';
import {
  getUserProfile,
  subscribe,
  updateMapType,
} from '../../../../redux/actions';
import text from '../../../../text';
import { amplitudeTrack } from '../../../../utilities';
import Button from '../../../button/Button';
import Icon from '../../../icon/Icon';
import Lottie from '../../../lottie/Lottie';
import { profileConstants } from '../../../../redux/constants';
import { rtkApi } from '../../../../redux/rtk';
import { MAP_TYPE } from '@larki/shared/types/user.type';

const FEATURES = [
  {
    icon: '2d-map',
    title: text('highRes2DMap'),
    description: text('startTrialFeature2d'),
  },
  {
    icon: 'file',
    title: text('fileManager'),
    description: text('startTrialFeatureFileManager'),
  },
  {
    icon: '3D',
    title: text('viewer3D'),
    description: text('startTrialFeature3d'),
  },
];

const DATA_FEATURES = [
  {
    icon: 'streetscape',
    title: text('streetscape3D'),
    description: text('startTrialStreetscapeFeature'),
    iconClassName: 'streetscape enabled',
  },
  {
    icon: 'aerial',
    title: text('aerial3D'),
    description: text('startTrialAerialFeature'),
    iconClassName: 'aerial enabled',
  },
];

const { ACCEPTED_INTRO_OFFER } = AMPLITUDE_EVENTS;

const StartTrialStep = ({ onCancel }) => {
  const { nextStep } = useWizard();
  const dispatch = useDispatch();
  const { currentUser, upsertMembership } = useSelector((state) => ({
    currentUser: state.profileReducer.userProfile,
    upsertMembership: state.profileReducer.upsertMembership,
  }));

  const handleClick = async () => {
    // Wait for the subscription process to complete so we
    // could have the latest user type e.g free, trial, etc
    await dispatch(
      subscribe({
        user: currentUser,
        priceId: MEMBERSHIP.MONTHLY.PRICE_ID,
        isTrial: true,
      })
    );

    dispatch(updateMapType(MAP_TYPE.HIGHRES, true));

    // legacy code to refetch the user profile
    dispatch(getUserProfile());

    // Invalidate the User tag in RTK Query cache
    // This is to ensure useUserProfile hook will refetch the user profile
    dispatch(rtkApi.util.invalidateTags(['User']));

    nextStep();
  };

  const trackCloseTrial = () => {
    amplitudeTrack(ACCEPTED_INTRO_OFFER.EVENT, {
      action: ACCEPTED_INTRO_OFFER.CLOSE_TRIAL,
    });
  };

  return (
    <>
      <Modal.Header
        className='center-content flex-column'
        onHide={() => trackCloseTrial()}
        close
        closeButton
      >
        <Lottie lottie='free-gift' />
        <h3>{text('freeTrialPlatform')}</h3>
      </Modal.Header>
      <Modal.Body>
        <h5 className='mt-2 mb-3 center-content'>
          {text('measureCollaboratePlatform')}
        </h5>
        <ul className='p-0'>
          {FEATURES.map((item, i) => {
            return (
              <li key={i} className='m-2 center-content'>
                <Icon
                  className='mr-3 framed outline-primary'
                  icon={item.icon}
                />
                <h6>
                  {item.title}{' '}
                  <span className='font-weight-normal'>{item.description}</span>
                </h6>
              </li>
            );
          })}
        </ul>

        <h5 className='mt-4 mb-3 center-content'>
          {text('getFreeDataDiscount', {
            amount: '49.50',
          })}
        </h5>
        <ul className='p-0'>
          {DATA_FEATURES.map((item, i) => {
            return (
              <li key={i} className='m-2 center-content'>
                <Icon
                  className={classNames('mr-3 framed outline-primary', {
                    [item.iconClassName]: item.iconClassName,
                  })}
                  icon={item.icon}
                />
                <h6>
                  {item.title}
                  <span className='font-weight-normal'>{item.description}</span>
                </h6>
              </li>
            );
          })}
        </ul>
      </Modal.Body>
      <Modal.Footer className='d-flex flex-column centered'>
        <Button
          loading={upsertMembership.isLoading}
          onClick={handleClick}
          autoFocus
        >
          {text('startTrial')}
        </Button>
        <Button
          variant='link'
          className='mt-2'
          onClick={() => {
            trackCloseTrial();
            onCancel();
          }}
          disabled={upsertMembership.isLoading}
        >
          {text('maybeLater')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default StartTrialStep;
